import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthenticatedFetch } from '../utils/api';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import { createSequentialPoller } from '../utils/sequentialPoller';

/**
 * useDashboardData — fetches and polls dashboard stats + sync status.
 *
 * Replaces the two inline useEffects that previously lived inside Dashboard.
 * Returns { stats, syncData, statsLoading, syncLoading, error, clearError, refreshStats, handleResyncProducts, handlePauseSync, handleResumeSync }
 */
export default function useDashboardData() {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const fetchRef = useRef(authenticatedFetch);
  useEffect(() => { fetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const [stats, setStats] = useState(null);
  const [syncData, setSyncData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(true);
  const [error, setError] = useState(null);
  // true while the sync-status API is being polled (the poller stops once the sync is not running)
  const [syncPolling, setSyncPolling] = useState(true);

  const syncPollerRef = useRef(null);
  const graceRoundsRef = useRef(0);
  const prevSyncRunningRef = useRef(null);
  const refreshStatsRef = useRef(null);

  // ── Stats fetcher (stored in ref so callers can trigger ad-hoc refreshes) ──
  const refreshStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      setError(null);
      const res = await fetchRef.current('/api/stats');
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(err.message || `Stats fetch failed: ${res.status}`);
      }
      setStats(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard statistics');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Keep a stable ref so sync polling can call it without re-creating
  useEffect(() => { refreshStatsRef.current = refreshStats; }, [refreshStats]);

  // Initial stats fetch
  useEffect(() => { refreshStats(); }, [refreshStats]);

  // ── Sync polling ──────────────────────────────────────────────────────────
  // Sequential polling (next request only after the previous one finishes), with
  // backoff on errors and a pause while the tab is hidden. Returns false to stop
  // once the sync has finished.
  useEffect(() => {
    let alive = true;

    const poll = async () => {
      try {
        const res = await fetchRef.current('/api/sync-status');
        if (!res || !res.ok) throw new Error(`Sync status fetch failed: ${res ? res.status : 'no response'}`);
        const data = await res.json();
        if (!alive) return false;

        const wasRunning = prevSyncRunningRef.current;
        const isRunning = data.isProductSyncRunning === true;

        setSyncData(data);
        setSyncLoading(false);

        // Refresh stats whenever sync state changes
        if (wasRunning !== null && wasRunning !== isRunning && refreshStatsRef.current) {
          refreshStatsRef.current();
        }
        prevSyncRunningRef.current = isRunning;

        // After a manual re-sync the backend flag may not be set yet, so keep
        // polling for a few rounds before treating "not running" as finished.
        if (!isRunning && graceRoundsRef.current > 0) {
          graceRoundsRef.current -= 1;
          return true;
        }

        // Stop polling once sync finishes
        return isRunning;
      } catch (err) {
        if (alive) setSyncLoading(false);
        throw err; // let the poller back off / give up after repeated failures
      }
    };

    const poller = createSequentialPoller(poll, {
      intervalMs: 5000,
      maxIntervalMs: 60000,
      onStop: () => { if (alive) setSyncPolling(false); },
    });
    syncPollerRef.current = poller;
    poller.start();

    return () => {
      alive = false;
      poller.stop();
      syncPollerRef.current = null;
    };
  }, []);

  // ── Re-sync action ────────────────────────────────────────────────────────
  const handleResyncProducts = useCallback(async () => {
    try {
      const res = await fetchRef.current('/api/resync-products', { method: 'POST' });
      if (res.ok) {
        showSuccessToast('Product sync started in background');
        refreshStatsRef.current?.();
        // Polling stops when a sync finishes, so restart it for this new sync
        graceRoundsRef.current = 3;
        setSyncPolling(true);
        syncPollerRef.current?.start();
      } else {
        const err = await res.json().catch(() => ({ message: 'Failed to start sync' }));
        showErrorToast(err.message || 'Failed to start sync');
      }
    } catch {
      showErrorToast('Failed to start sync');
    }
  }, []);

  // ── Pause / resume the product sync ───────────────────────────────────────
  // Pause: the backend finishes the current product, then stops (polling keeps running
  // until the status flips from "pausing" to "paused", then stops by itself).
  const handlePauseSync = useCallback(async () => {
    try {
      const res = await fetchRef.current('/api/pause-sync', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showSuccessToast(data.message || 'Pausing sync after the current product…');
        setSyncPolling(true);
        syncPollerRef.current?.start(); // no-op if already polling; makes sure status updates arrive
      } else {
        showErrorToast(data.message || 'Failed to pause sync');
      }
    } catch {
      showErrorToast('Failed to pause sync');
    }
  }, []);

  const handleResumeSync = useCallback(async () => {
    try {
      const res = await fetchRef.current('/api/resume-sync', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        showSuccessToast(data.message || 'Product sync resumed');
        graceRoundsRef.current = 3;
        setSyncPolling(true);
        syncPollerRef.current?.start();
      } else {
        showErrorToast(data.message || 'Failed to resume sync');
      }
    } catch {
      showErrorToast('Failed to resume sync');
    }
  }, []);

  return {
    stats,
    syncData,
    statsLoading,
    syncLoading,
    syncPolling,
    error,
    clearError: () => setError(null),
    refreshStats,
    handleResyncProducts,
    handlePauseSync,
    handleResumeSync,
  };
}
